import React from "react";
import { Card, Heading, Link, TextStyle, Badge } from "@shopify/polaris";

function fullName(person) {
  if (!person) return "";
  return [person.first_name, person.last_name].filter(Boolean).join(" ");
}

function formatAddress(addr) {
  if (!addr) return [];
  const street = [addr.address_1, addr.address_2].filter(Boolean).join(" ");
  const cityLine = [addr.city, addr.state, addr.postcode]
    .filter(Boolean)
    .join(", ");
  return [fullName(addr), addr.company, street, cityLine, addr.country].filter(
    Boolean
  );
}

function sameAddress(a, b) {
  if (!a || !b) return false;
  return (
    a.address_1 === b.address_1 &&
    a.address_2 === b.address_2 &&
    a.city === b.city &&
    a.state === b.state &&
    a.postcode === b.postcode &&
    a.country === b.country
  );
}

function statusTone(status) {
  switch (status) {
    case "completed":
      return "success";
    case "processing":
    case "on-hold":
      return "info";
    case "cancelled":
    case "refunded":
    case "failed":
      return "critical";
    default:
      return undefined;
  }
}

function formatTotal(total, currency) {
  const amount = parseFloat(total);
  if (Number.isNaN(amount)) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch (_e) {
    return `${currency || ""} ${total}`.trim();
  }
}

export default function OrderDetailsCard({ order }) {
  if (!order) return null;

  const billing = order.billing || {};
  const shipping = order.shipping || {};
  const customer = order.customer;

  const customerName = fullName(customer) || fullName(billing);
  const customerEmail = customer?.email || billing.email;
  const customerPhone = billing.phone;
  const shippingLines = formatAddress(shipping);
  const billingLines = formatAddress(billing);
  const billingMatchesShipping = sameAddress(billing, shipping);

  const orderLink = order.admin_url ? (
    <Link url={order.admin_url} external>
      Order #{order.id}
    </Link>
  ) : (
    <Heading>Order #{order.id}</Heading>
  );

  return (
    <Card
      title={
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
        >
          {orderLink}
          {order.status && (
            <Badge status={statusTone(order.status)}>{order.status}</Badge>
          )}
        </div>
      }
    >
      {(customerName || customerEmail || customerPhone) && (
        <Card.Section title="Customer">
          {customerName ? (
            customer?.admin_url ? (
              <Link url={customer.admin_url} external>
                {customerName}
              </Link>
            ) : (
              customerName
            )
          ) : (
            <TextStyle variation="subdued">Guest checkout</TextStyle>
          )}
          {customerEmail && (
            <>
              <br />
              {customerEmail}
            </>
          )}
          {customerPhone && (
            <>
              <br />
              {customerPhone}
            </>
          )}
        </Card.Section>
      )}

      {shippingLines.length > 0 && (
        <Card.Section title="Shipping address">
          {shippingLines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </Card.Section>
      )}

      {billingLines.length > 0 && (
        <Card.Section title="Billing address">
          {billingMatchesShipping ? (
            <TextStyle variation="subdued">Same as shipping address.</TextStyle>
          ) : (
            billingLines.map((line, i) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))
          )}
        </Card.Section>
      )}

      {order.payment_method_title && (
        <Card.Section title="Payment">
          {order.payment_method_title}
        </Card.Section>
      )}

      {order.total && (
        <Card.Section title="Total">
          {formatTotal(order.total, order.currency) ?? order.total}
        </Card.Section>
      )}
    </Card>
  );
}
